import { useCallback } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { ChatNewPayload, SetChatContextPayload } from '@atlaskit/rovo-triggers/types';

const BUILD_AGENT_SKILL_SLUG = 'build-agent';
const INLINE_CREATE_AGENT_ACTION_SUBJECT_ID = 'inlineCreateAgentItem';
const INLINE_CREATE_AGENT_CONTEXT_KEY = 'jiraAgentCreateContext';
const INLINE_CREATE_AGENT_FLOW_KEY = 'build_and_chat';
const INLINE_CREATE_AGENT_SOURCE_ID = 'confluence-inline-create-agent-menu';
const ROVO_SKILL_EXTENSION_TYPE = 'com.atlassian.rovo.skill';
const ROVO_SKILL_EXTENSION_KEY = 'skill:skillTag';

export const INLINE_CREATE_AGENT_SKILL_TOKEN = '__BUILD_AGENT_SKILL__';

type UseInlineCreateAgentItemClickHandlerArgs = {
	onClick: (actionSubjectId: string) => void;
	promptText: string;
	source: string;
};

type InlineCreateAgentPromptAdf = {
	type: 'doc';
	version: 1;
	content: [
		{
			type: 'paragraph';
			content: Array<
				| {
						type: 'text';
						text: string;
				  }
				| {
						type: 'inlineExtension';
						attrs: {
							extensionType: typeof ROVO_SKILL_EXTENSION_TYPE;
							extensionKey: typeof ROVO_SKILL_EXTENSION_KEY;
							parameters: { slug: typeof BUILD_AGENT_SKILL_SLUG };
						};
				  }
			>;
		},
	];
};

const createInlineCreateAgentPrompt = (localizedPrompt: string): InlineCreateAgentPromptAdf => {
	const skillTokenIndex = localizedPrompt.indexOf(INLINE_CREATE_AGENT_SKILL_TOKEN);
	const hasSkillToken = skillTokenIndex !== -1;
	let textBefore = '';
	let textAfter = '';

	if (hasSkillToken) {
		textBefore = localizedPrompt.slice(0, skillTokenIndex);
		textAfter = localizedPrompt
			.slice(skillTokenIndex + INLINE_CREATE_AGENT_SKILL_TOKEN.length)
			.split(INLINE_CREATE_AGENT_SKILL_TOKEN)
			.join('');
	} else if (localizedPrompt) {
		textBefore = `${localizedPrompt} `;
	}
	const content: InlineCreateAgentPromptAdf['content'][0]['content'] = [];

	if (textBefore) {
		content.push({ type: 'text', text: textBefore });
	}

	content.push({
		type: 'inlineExtension',
		attrs: {
			extensionType: ROVO_SKILL_EXTENSION_TYPE,
			extensionKey: ROVO_SKILL_EXTENSION_KEY,
			parameters: { slug: BUILD_AGENT_SKILL_SLUG },
		},
	});

	if (textAfter) {
		content.push({ type: 'text', text: textAfter });
	}

	return {
		type: 'doc',
		version: 1,
		content: [
			{
				type: 'paragraph',
				content,
			},
		],
	};
};

const createInlineCreateAgentSetContextPayload = (
	interactionSource: string,
): SetChatContextPayload => ({
	type: 'set-message-context',
	source: INLINE_CREATE_AGENT_SOURCE_ID,
	interactionSource,
	product: 'confluence',
	openChat: false,
	data: {
		contextKey: INLINE_CREATE_AGENT_CONTEXT_KEY,
		setContext: <T>(chatContext: T): T =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				...(typeof chatContext === 'object' && chatContext !== null ? chatContext : {}),
				flowKey: INLINE_CREATE_AGENT_FLOW_KEY,
			}) as T,
	},
});

const createInlineCreateAgentChatNewPayload = (
	interactionSource: string,
	promptText: string,
): ChatNewPayload => ({
	type: 'chat-new',
	source: INLINE_CREATE_AGENT_SOURCE_ID,
	interactionSource,
	product: 'confluence',
	openChatMode: 'sidebar',
	data: {
		dialogues: [],
		prompt: createInlineCreateAgentPrompt(promptText),
		sourceId: INLINE_CREATE_AGENT_SOURCE_ID,
		skipCreatingSeededConversation: true,
		minionAlias: BUILD_AGENT_SKILL_SLUG,
	},
});

export const useInlineCreateAgentItemClickHandler = ({
	onClick,
	promptText,
	source,
}: UseInlineCreateAgentItemClickHandlerArgs) => {
	const publish = usePublish('ai-mate');

	return useCallback(() => {
		publish(createInlineCreateAgentSetContextPayload(source));
		publish(createInlineCreateAgentChatNewPayload(source, promptText));
		onClick(INLINE_CREATE_AGENT_ACTION_SUBJECT_ID);
	}, [onClick, promptText, publish, source]);
};
